import {
  ActionIcon,
  Badge,
  Box,
  Card,
  Flex,
  Grid,
  MultiSelect,
  NumberFormatter,
  Select,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IIssueApi, IIssueUpdateSchemaProps } from "@schemas/issueSchema";
import {
  ICapitalLetter,
  IOtherAttributeConfigApi,
  IOtherAttributeConfigItemProps,
} from "@schemas/otherAttributesSchema";
import { IconCalendarWeek, IconInfoCircle, IconMathFunction, IconNumbers, IconSparkles } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { McTitle } from "..";
import {
  PutProjectByProjectIdIssueUpdateSimpleApiArg,
  usePutProjectByProjectIdIssueUpdateSimpleMutation,
} from "../../../openapi";
import { formatNumberNb } from "../../app/utils";
import FoldableSection from "../FoldableSection/FoldableSection";
import McInputNew from "../McInput/McInputNew";
import McTextAreaNew from "../McInput/McTextAreaNew";
import SimpleStampCheck from "../SimpleStampCheck/SimpleStampCheck";
import IssueBreeamCriterionCheckModal from "../../app/routes/issuesApp/IssuesTab/IssueItemModal/new/IssueBreeamCriterionCheckModal";
import IssueFormulaCounterInput from "../../app/routes/issuesApp/IssuesTab/IssueItemModal/new/FormulaCounterSuggestion/IssueFormulaCounterInput";

type IConfigWithId = { fieldId: string; span: number } & IOtherAttributeConfigItemProps;

interface ISection {
  sectionName: string;
  sortedAttributeConfigs: ([IConfigWithId, IConfigWithId?, IConfigWithId?] | [IConfigWithId])[];
}

const BREEAM_ACTION_ATTRIBUTE_LABELS = ["Tekst til BRE online tool"];

const normalizeAttributeLabel = (label?: string | null) => label?.trim().toUpperCase() ?? "";

const shouldShowBreeamActionForLabel = (label?: string | null) => {
  const normalizedLabel = normalizeAttributeLabel(label);

  return BREEAM_ACTION_ATTRIBUTE_LABELS.some((term) => normalizeAttributeLabel(term) === normalizedLabel);
};

function CustomAttributeSectionMantine<T>({
  issue,
  projectId,
  attributeConfig = {},
  data = {} as T,
}: {
  issue: IIssueApi;
  projectId: string;
  attributeConfig: IOtherAttributeConfigApi;
  data: T;
}) {
  if (issue == null) {
    return null;
  }

  const [unlockedFields, setUnlockedFields] = useState<string[]>([]);
  const [breeamModalOpen, setBreeamModalOpen] = useState(false);
  const [updateValue] = usePutProjectByProjectIdIssueUpdateSimpleMutation();

  const formatMathFormulaTooltip = (expression?: string) => {
    if (!expression) return "";

    return expression.replace(/\b[A-Z]+\b/g, (attributeId) => {
      const label = attributeConfig[attributeId]?.label;
      return label ? `${attributeId} (${label})` : attributeId;
    });
  };

  const formatFormulaTooltip = (cell: IConfigWithId) => {
    if (cell.typeOfValue !== "FORMULA" || cell.formula == null) return "";

    return cell.formula.confArr
      .map((conf) => {
        if (conf.type === "COUNTER") return "Löpnummer";
        if (conf.type === "DELIMITER") return conf.value;

        const label = attributeConfig[conf.value]?.label;
        return label ? `${conf.value} (${label})` : conf.value;
      })
      .join(" ");
  };

  const formatReadOnlyValue = (
    typeOfValue: IOtherAttributeConfigItemProps["typeOfValue"],
    value: unknown,
  ): string | number => {
    if (typeOfValue === "MATH_FORMULA") {
      return formatNumberNb(value);
    }

    return typeof value === "string" || typeof value === "number" ? value : "";
  };

  const u = (prop: IIssueUpdateSchemaProps): PutProjectByProjectIdIssueUpdateSimpleApiArg => {
    return {
      projectId,
      body: {
        _id: issue._id,
        property: prop as PutProjectByProjectIdIssueUpdateSimpleApiArg["body"]["property"],
      },
    };
  };

  const toggleLockdeField = (fieldId: string) => {
    setUnlockedFields((prev) => (prev.includes(fieldId) ? prev.filter((id) => id !== fieldId) : [...prev, fieldId]));
  };

  const sections = useMemo(() => {
    const fields = Object.keys(attributeConfig);

    // sort attributes by order
    fields.sort((a, b) => attributeConfig[a].order - attributeConfig[b].order);

    // remove inactive attributes
    const activeFields = fields.filter((attribute) => attributeConfig[attribute].isActive === true);

    // group by section
    const sectionKeysMap: Map<string, IConfigWithId[]> = new Map();

    activeFields.forEach((fieldId) => {
      const typeOfValue = attributeConfig[fieldId].typeOfValue;
      const span = typeOfValue === "MULTILINESTRING" ? 12 : 4;
      const sectionName = attributeConfig[fieldId].section;

      if (!sectionKeysMap.has(sectionName)) {
        sectionKeysMap.set(sectionName, []);
      }

      sectionKeysMap.get(sectionName)!.push({ ...attributeConfig[fieldId], fieldId, span });
    });

    // layout into rows of 3 (with MULTILINESTRING spanning full row)
    const sectionsArray: ISection[] = Array.from(sectionKeysMap).map(([sectionName, attributeConfigs]) => {
      const attributeConfigRows: ([IConfigWithId, IConfigWithId?, IConfigWithId?] | [IConfigWithId])[] = [];
      let rowOfThree: [IConfigWithId, IConfigWithId?, IConfigWithId?] = [null, null, null];
      let columnIndex = 0;

      for (let i = 0; i < attributeConfigs.length; i++) {
        const cfg = attributeConfigs[i];

        if (cfg.typeOfValue === "MULTILINESTRING") {
          if (rowOfThree[0] || rowOfThree[1] || rowOfThree[2]) {
            attributeConfigRows.push(rowOfThree);
            rowOfThree = [null, null, null];
          }
          attributeConfigRows.push([cfg]);
          columnIndex = 0;
          continue;
        }

        rowOfThree[columnIndex] = cfg;
        columnIndex += 1;

        if (columnIndex === 3) {
          attributeConfigRows.push(rowOfThree);
          rowOfThree = [null, null, null];
          columnIndex = 0;
        }

        if (i === attributeConfigs.length - 1 && (rowOfThree[0] || rowOfThree[1] || rowOfThree[2])) {
          attributeConfigRows.push(rowOfThree);
        }
      }

      return { sectionName, sortedAttributeConfigs: attributeConfigRows };
    });

    return sectionsArray.map((section, index) => (
      <FoldableSection
        key={section.sectionName}
        storageKey={`issue-${issue.issueBoard._id}-other-attributes-section-${index}`}
      >
        <FoldableSection.Header>
          <Title order={6}>{section.sectionName}</Title>
        </FoldableSection.Header>
        <FoldableSection.Body>
          <Card w="100%" bg="#f8f9fa" bd="1px solid var(--mantine-color-warmgray-2)">
            <Grid flex={1}>
              {section.sortedAttributeConfigs.flatMap((rows, iColumn) =>
                rows.map((cell, iField) => {
                  const cellKey = `${index}${iColumn}${iField}`;

                  if (cell == null) {
                    return <div key={cellKey} />;
                  }

                  const cellValue = (data as any)[cell.fieldId];
                  const shouldShowBreeamAction =
                    issue.taskOrIssue === "ISSUE" && shouldShowBreeamActionForLabel(cell.label);

                  return (
                    <Grid.Col span={cell.span} key={cellKey}>
                      <McTitle title={cell.label} className="h-100">
                        <McTitle.InlineData positionInlineData="before">
                          <Text pt={4} size="xs">
                            {cell?.labelColor && (
                              <i
                                className="fa fa-fw fa-circle"
                                style={{ color: cell?.labelColor, marginLeft: "-0.2rem" }}
                              />
                            )}
                          </Text>
                        </McTitle.InlineData>

                        <McTitle.InlineData positionInlineData="flex-end">
                          <Flex gap="xs" align="center" wrap="nowrap">
                            {shouldShowBreeamAction && (
                              <Tooltip
                                label="Kjør AI-analyse av tekst til BRE online tool"
                                withArrow
                                zIndex={2042}
                                position="top-end"
                              >
                                <ActionIcon
                                  variant="light"
                                  color="violet"
                                  radius="xl"
                                  size="lg"
                                  aria-label="Kjør BREEAM AI-analyse"
                                  onClick={() => setBreeamModalOpen(true)}
                                  style={{
                                    boxShadow: "0 0 0 1px var(--mantine-color-violet-2) inset",
                                  }}
                                >
                                  <IconSparkles size={18} />
                                </ActionIcon>
                              </Tooltip>
                            )}
                            {cell.typeOfValue === "MATH_FORMULA" && (
                              <Badge size="xs" color="yellow" variant="light">
                                BETA
                              </Badge>
                            )}
                            <Text size="sm" style={{ whiteSpace: "nowrap" }}>
                              {cell.locked === true &&
                                cell.typeOfValue !== "FORMULA" &&
                                cell.typeOfValue !== "MATH_FORMULA" && (
                                  <i
                                    className={`fa fa-fw pointer ${
                                      unlockedFields.includes(cellKey) ? "fa-unlock text-success" : "fa-lock text-muted"
                                    }`}
                                    onClick={() => toggleLockdeField(cellKey)}
                                  />
                                )}
                              {cell.description && (
                                <Tooltip label={cell.description} withArrow zIndex={2042}>
                                  <i className="fa fa-info-circle fa-fw text-info" />
                                </Tooltip>
                              )}
                            </Text>
                          </Flex>
                        </McTitle.InlineData>

                        {cell.typeOfValue === "BOOLEAN" && (
                          <Flex gap="xs" h="100%" align="center">
                            <SimpleStampCheck
                              disabled={cell.locked === true && unlockedFields.includes(cellKey) === false}
                              toggled={cellValue == null ? null : Boolean(cellValue)}
                              toggle={(newValue) => {
                                if (newValue === cellValue) return;

                                updateValue(u({ otherAttributesData: { [cell.fieldId]: newValue } }));
                                if (cell.locked === true) toggleLockdeField(cellKey);
                              }}
                            />
                          </Flex>
                        )}

                        {cell.typeOfValue === "DROPDOWN" && cell.allowMultipleSelectionsInDropdown !== true && (
                          <OtherAttributesDropdown
                            disabled={cell.locked === true && unlockedFields.includes(cellKey) === false}
                            cell={cell}
                            cellValue={cellValue}
                            onSave={(newValue) => {
                              if (newValue === cellValue) return;

                              updateValue(u({ otherAttributesData: { [cell.fieldId]: newValue ?? null } }));
                              if (cell.locked === true) toggleLockdeField(cellKey);
                            }}
                          />
                        )}

                        {cell.typeOfValue === "DROPDOWN" &&
                          Array.isArray(cell.tableOptions) &&
                          cell.allowMultipleSelectionsInDropdown === true && (
                            <MultiSelect
                              styles={{
                                input: {
                                  minHeight: "2.625rem",
                                  height: "auto",
                                },
                              }}
                              disabled={cell.locked === true && unlockedFields.includes(cellKey) === false}
                              value={convertValueToArray(cellValue)}
                              data={
                                cell.tableOptions?.map((option) => ({
                                  value: String(option.value),
                                  label:
                                    cell.dropdownOptionType === "NUMBER"
                                      ? getNumberDropdownLabel(option.value)
                                      : String(option.value),
                                })) || []
                              }
                              renderOption={({ option }) => (
                                <DropdownOptionLabel
                                  value={option.value}
                                  isNumber={cell.dropdownOptionType === "NUMBER"}
                                />
                              )}
                              onChange={(value) => {
                                const currentValue = convertValueToArray(cellValue);
                                const normalizedValue = mapDropdownStringsToValues(value, cell.dropdownOptionType);
                                const hasChanged =
                                  JSON.stringify([...currentValue].sort()) !== JSON.stringify([...value].sort());

                                if (hasChanged) {
                                  updateValue(u({ otherAttributesData: { [cell.fieldId]: normalizedValue } }));
                                }
                              }}
                            />
                          )}

                        {cell.typeOfValue === "STRING" && (
                          <McInputNew
                            disabled={cell.locked === true && unlockedFields.includes(cellKey) === false}
                            type="text"
                            onBlur={(e) => {
                              if (e.target.value === cellValue) return;

                              updateValue(u({ otherAttributesData: { [cell.fieldId]: e.target.value } }));
                              if (cell.locked === true) toggleLockdeField(cellKey);
                            }}
                            value={cellValue}
                          />
                        )}

                        {cell.typeOfValue === "MULTILINESTRING" && (
                          <McTextAreaNew
                            disabled={cell.locked === true && unlockedFields.includes(cellKey) === false}
                            autosize
                            onBlur={(e) => {
                              if (e.target.value === cellValue) return;

                              updateValue(u({ otherAttributesData: { [cell.fieldId]: e.target.value } }));
                              if (cell.locked === true) toggleLockdeField(cellKey);
                            }}
                            value={cellValue}
                          />
                        )}

                        {cell.typeOfValue === "NUMBER" && (
                          <McInputNew
                            rightSection={<IconNumbers color="var(--mantine-color-warmgray-3)" />}
                            disabled={cell.locked === true && unlockedFields.includes(cellKey) === false}
                            type="number"
                            thousandSeparator=" "
                            onBlur={(e) => {
                              const newValue = e.target.value === "" ? null : Number(e.target.value);
                              if (newValue === cellValue) return;

                              updateValue(u({ otherAttributesData: { [cell.fieldId]: newValue } }));
                              if (cell.locked === true) toggleLockdeField(cellKey);
                            }}
                            value={cellValue}
                          />
                        )}

                        {cell.typeOfValue === "DATE" && (
                          <DateInput
                            styles={{ input: { minHeight: "2.625rem", height: "2.625rem" } }}
                            clearable
                            valueFormat="DD.MM.YYYY"
                            disabled={cell.locked === true && unlockedFields.includes(cellKey) === false}
                            onChange={(e) => {
                              if (e === cellValue) return;

                              updateValue(
                                u({
                                  otherAttributesData: {
                                    [cell.fieldId]: e != null ? `${e}T00:00:00.000Z` : e,
                                  },
                                }),
                              );
                              if (cell.locked === true) toggleLockdeField(cellKey);
                            }}
                            value={cellValue}
                            rightSection={
                              cellValue == null && <IconCalendarWeek color="var(--mantine-color-warmgray-3)" />
                            }
                          />
                        )}

                        {(cell.typeOfValue === "FORMULA" || cell.typeOfValue === "MATH_FORMULA") && (
                          <>
                            {cell.typeOfValue === "FORMULA" &&
                            cell.formula?.counterMode === "MANUAL" &&
                            cell.formula.confArr.some((conf) => conf.type === "COUNTER") ? (
                              <Box
                                p="xs"
                                bg="orange.0"
                                style={{
                                  border: "1px solid var(--mantine-color-orange-2)",
                                  borderRadius: "var(--mantine-radius-sm)",
                                }}
                              >
                                <Flex gap="sm" align="flex-end" wrap="wrap">
                                  <Tooltip label={formatFormulaTooltip(cell)} withArrow zIndex={2042}>
                                    <Flex
                                      gap="xs"
                                      align="center"
                                      h="2.625rem"
                                      style={{ flex: "1 1 18rem", minWidth: 0 }}
                                    >
                                      <Text truncate fw={500}>
                                        {formatReadOnlyValue(cell.typeOfValue, cellValue)}
                                      </Text>
                                      <IconMathFunction size={18} color="var(--mantine-color-warmgray-3)" />
                                    </Flex>
                                  </Tooltip>
                                  <Box style={{ flex: "0 0 12rem" }}>
                                    <IssueFormulaCounterInput formulaAttributeKey={cell.fieldId as ICapitalLetter} />
                                  </Box>
                                </Flex>
                              </Box>
                            ) : (
                              <TextInput
                                styles={{ input: { backgroundColor: "transparent  " } }}
                                value={formatReadOnlyValue(cell.typeOfValue, cellValue)}
                                readOnly
                                rightSection={
                                  <Flex gap={6} align="center">
                                    {cell.typeOfValue === "MATH_FORMULA" && cell.mathFormula?.expression && (
                                      <Tooltip
                                        label={formatMathFormulaTooltip(cell.mathFormula.expression)}
                                        withArrow
                                        zIndex={2042}
                                      >
                                        <span style={{ display: "flex", alignItems: "center" }}>
                                          <IconInfoCircle size={16} color="var(--mantine-color-blue-6)" />
                                        </span>
                                      </Tooltip>
                                    )}
                                    <IconMathFunction color="var(--mantine-color-warmgray-3)" />
                                  </Flex>
                                }
                              />
                            )}
                          </>
                        )}
                      </McTitle>
                    </Grid.Col>
                  );
                }),
              )}
            </Grid>
          </Card>
        </FoldableSection.Body>
      </FoldableSection>
    ));
  }, [attributeConfig, data, unlockedFields, updateValue, projectId, issue]);

  return (
    <>
      {breeamModalOpen && <IssueBreeamCriterionCheckModal onClose={() => setBreeamModalOpen(false)} />}
      {sections}
    </>
  );
}

export default CustomAttributeSectionMantine;

const convertValueToArray = (value: unknown): string[] => {
  try {
    if (value == null) return [];
    if (typeof value === "string") return [value];
    if (typeof value === "number") return [value.toString()];
    if (Array.isArray(value) && value.every((v) => typeof v === "string" || typeof v === "number")) {
      return value.map((v) => String(v));
    }
    return [];
  } catch {
    return [];
  }
};

const mapDropdownStringsToValues = (
  value: string[],
  dropdownOptionType: IOtherAttributeConfigItemProps["dropdownOptionType"],
): (string | number)[] => {
  if (dropdownOptionType !== "NUMBER") {
    return value;
  }

  return value.map((item) => Number(item)).filter((item) => Number.isNaN(item) === false && Number.isFinite(item));
};

const getNumberDropdownLabel = (value: unknown) => {
  const numberValue = typeof value === "number" ? value : Number(value);

  return Number.isFinite(numberValue) ? formatNumberNb(numberValue) : String(value ?? "");
};

const DropdownOptionLabel = ({ value, isNumber }: { value: unknown; isNumber: boolean }) => {
  if (!isNumber) {
    return <>{String(value ?? "")}</>;
  }

  const numberValue = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numberValue)) {
    return <>{String(value ?? "")}</>;
  }

  return <NumberFormatter value={numberValue} thousandSeparator=" " decimalSeparator="," />;
};

const OtherAttributesDropdown: React.FC<{
  cell: IConfigWithId;
  cellValue: any;
  onSave: (newValue: string | number | null) => void;
  disabled?: boolean;
}> = ({ cell, cellValue, onSave, disabled = false }) => {
  const isNumberDropdown = cell.dropdownOptionType === "NUMBER";
  const _options = useMemo(
    () =>
      cell?.tableOptions?.map((option) => ({
        value: String(option._id ?? option.value),
        label: isNumberDropdown ? getNumberDropdownLabel(option.value) : String(option.value),
        rawValue: option.value,
        color: option?.color,
        valueAndDescription: `${isNumberDropdown ? getNumberDropdownLabel(option.value) : String(option.value)}${
          option.description != null && option.description !== "" ? " - " + option.description : ""
        }`,
      })),
    [cell?.tableOptions, isNumberDropdown],
  );

  return (
    <Select
      disabled={disabled}
      value={_options?.find((o) => String(o.rawValue) === String(cellValue))?.value ?? String(cellValue ?? "")}
      data={_options || []}
      renderOption={({ option }) => {
        const rawValue = _options?.find((item) => item.value === option.value)?.rawValue ?? option.label;

        return <DropdownOptionLabel value={rawValue} isNumber={isNumberDropdown} />;
      }}
      onClear={cellValue != null ? () => onSave(null) : undefined}
      onChange={(selectedValue) => {
        const selectedOption = _options?.find((option) => option.value === selectedValue);
        onSave(selectedOption?.rawValue ?? null);
      }}
    />
  );
};
